import type { Device, FrameToShell, MediaRef, ShellToFrame } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { Toaster, toast } from "@aurumiq/ui/components/sonner";
import { ConfirmDialog } from "@aurumiq/ui/composite/confirm-dialog";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { useTheme } from "@aurumiq/ui/hooks/use-theme";
import { cn } from "@aurumiq/ui/lib/utils";
import {
  ArrowLeft,
  ExternalLink,
  Eye,
  FileText,
  History,
  Layers,
  ListChecks,
  Monitor,
  Palette,
  PenLine,
  Plus,
  Redo2,
  Rocket,
  Save,
  Smartphone,
  Tablet,
  Undo2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { env } from "@/lib/env";
import type { CmsPreviewToken } from "@/lib/types";
import { MediaPickerDialog } from "../media-picker";
import { usePreviewToken } from "../queries";
import { Canvas, frameSrc } from "./canvas";
import { Inspector, type InspectorTab } from "./inspector";
import { AddPanel, blockTitle, DesignPanel, HistoryPanel, LayersPanel, PagePanel } from "./panels";
import { useDesignerState } from "./use-designer-state";

type LeftTab = "add" | "layers" | "page" | "design" | "history";

const LEFT_TABS: { key: LeftTab; label: string; icon: typeof Plus }[] = [
  { key: "add", label: "Add", icon: Plus },
  { key: "layers", label: "Layers", icon: Layers },
  { key: "page", label: "Page", icon: FileText },
  { key: "design", label: "Design", icon: Palette },
  { key: "history", label: "History", icon: History },
];

const DEVICES: { key: Device; label: string; icon: typeof Monitor }[] = [
  { key: "desktop", label: "Desktop", icon: Monitor },
  { key: "tablet", label: "Tablet · 834px", icon: Tablet },
  { key: "mobile", label: "Mobile · 390px", icon: Smartphone },
];

/** Origin the canvas frame lives on; every postMessage names it and every listener checks it. */
export const LANDING_ORIGIN = new URL(env.landingUrl).origin;

const MESSAGE_PREFIX = "aurumiq:editor:";

function isTypingTarget(el: EventTarget | null) {
  const t = el as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

const railBtn =
  "flex w-12 flex-col items-center gap-0.5 rounded-xl py-2 font-semibold text-[10px] transition";
const pillBtn =
  "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 font-semibold text-[13px] text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-30";

/** Full-screen page designer. Remounts when the page id changes so state and frame reload. */
export function DesignerPage() {
  const { id = "" } = useParams();
  return <DesignerInner key={id} pageId={id} />;
}

function DesignerInner({ pageId }: { pageId: string }) {
  const navigate = useNavigate();
  const { resolved: theme } = useTheme();
  const st = useDesignerState(pageId);
  const previewToken = usePreviewToken(pageId);

  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frameReady, setFrameReady] = useState(false);
  const [token, setToken] = useState<CmsPreviewToken | null>(null);
  const [leftTab, setLeftTab] = useState<LeftTab | null>("layers");
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("content");
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [revertTo, setRevertTo] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ requestId: string; id: string; path: string } | null>(
    null,
  );

  /* ---------- frame plumbing ---------- */

  const savedSlug = st.detail?.slug ?? "";
  const requestToken = previewToken.mutate;
  useEffect(() => {
    if (savedSlug) requestToken(undefined, { onSuccess: setToken });
  }, [savedSlug, requestToken]);
  const src = useMemo(() => (token ? frameSrc(token, savedSlug) : null), [token, savedSlug]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new src reloads the frame
  useEffect(() => setFrameReady(false), [src]);

  const post = useCallback((msg: ShellToFrame) => {
    frameRef.current?.contentWindow?.postMessage(msg, LANDING_ORIGIN);
  }, []);

  const sendState = useCallback(() => {
    if (!st.previewPage) return;
    post({
      type: "aurumiq:editor:state",
      page: st.previewPage,
      selectedId: st.selectedId,
      mode: st.mode,
      device: st.device,
    });
  }, [post, st.previewPage, st.selectedId, st.mode, st.device]);
  const sendStateRef = useRef(sendState);
  sendStateRef.current = sendState;

  useEffect(() => {
    if (!frameReady) return;
    const t = window.setTimeout(sendState, 150);
    return () => window.clearTimeout(t);
  }, [frameReady, sendState]);

  useEffect(() => {
    if (frameReady) post({ type: "aurumiq:editor:mode", mode: st.mode });
  }, [frameReady, st.mode, post]);

  const scrollTo = useCallback(
    (blockId: string) => {
      if (frameReady) post({ type: "aurumiq:editor:scrollTo", id: blockId });
    },
    [frameReady, post],
  );

  /* ---------- actions shared by chrome, keyboard and frame ---------- */

  /** Blocks that exist in the saved draft ask for confirmation; unsaved ones just go. */
  const requestDelete = useCallback(
    (blockId: string) => {
      if (!st.page?.blocks.some((b) => b.id === blockId)) return;
      const persisted = st.detail?.draft.blocks.some((b) => (b as { id?: unknown }).id === blockId);
      if (persisted) setConfirmDelete(blockId);
      else st.deleteBlock(blockId);
    },
    [st],
  );

  const openInspector = useCallback(
    (blockId: string, tab: InspectorTab) => {
      st.setSelectedId(blockId);
      setInspectorTab(tab);
      setInspectorOpen(true);
    },
    [st],
  );

  const doSave = useCallback(async () => {
    if (!st.dirty || st.saving) return;
    await st.save().catch(() => null);
  }, [st]);

  /* ---------- frame → shell ---------- */

  const handleFrame = (m: FrameToShell) => {
    switch (m.type) {
      case "aurumiq:editor:ready":
        setFrameReady(true);
        window.setTimeout(() => sendStateRef.current(), 0);
        break;
      case "aurumiq:editor:select":
        st.setSelectedId(m.id);
        if (m.id) setInspectorOpen(true);
        break;
      case "aurumiq:editor:hover":
        st.setHoveredId(m.id);
        break;
      case "aurumiq:editor:setField":
        st.setField(m.id, m.path, m.value);
        break;
      case "aurumiq:editor:setBlock":
        st.setBlock(m.id, m.block);
        break;
      case "aurumiq:editor:move":
        st.moveBlock(m.id, m.toIndex);
        break;
      case "aurumiq:editor:insert":
        st.insertBlock(m.blockType, m.index);
        setInspectorOpen(true);
        break;
      case "aurumiq:editor:action":
        switch (m.action) {
          case "duplicate":
            st.duplicateBlock(m.id);
            break;
          case "delete":
            requestDelete(m.id);
            break;
          case "toggle":
            st.toggleHidden(m.id);
            break;
          case "up":
            st.moveBy(m.id, -1);
            break;
          case "down":
            st.moveBy(m.id, 1);
            break;
          case "settings":
            openInspector(m.id, "content");
            break;
          case "style":
            openInspector(m.id, "style");
            break;
          case "add-after":
            st.setSelectedId(m.id);
            setLeftTab("add");
            break;
        }
        break;
      case "aurumiq:editor:replaceImage":
        setPicker({ requestId: m.requestId, id: m.id, path: m.path });
        break;
      case "aurumiq:editor:height":
        break;
      case "aurumiq:editor:shortcut":
        if (m.key === "undo") st.undo();
        else if (m.key === "redo") st.redo();
        else if (m.key === "save") void doSave();
        break;
    }
  };
  const handleFrameRef = useRef(handleFrame);
  handleFrameRef.current = handleFrame;

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== LANDING_ORIGIN) return;
      const data = e.data as { type?: unknown } | null;
      if (!data || typeof data !== "object" || typeof data.type !== "string") return;
      if (!data.type.startsWith(MESSAGE_PREFIX)) return;
      handleFrameRef.current(data as FrameToShell);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const answerPicker = (media: MediaRef | null) => {
    if (!picker) return;
    if (media) st.setField(picker.id, picker.path, media);
    post({
      type: "aurumiq:editor:media",
      requestId: picker.requestId,
      media: media?.url ? { ...media, url: media.url } : null,
    });
    setPicker(null);
  };

  /* ---------- keyboard / unload / title ---------- */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      const k = e.key.toLowerCase();
      if (meta && k === "s") {
        e.preventDefault();
        void doSave();
        return;
      }
      if (meta && k === "z") {
        e.preventDefault();
        if (e.shiftKey) st.redo();
        else st.undo();
        return;
      }
      if (meta && k === "y") {
        e.preventDefault();
        st.redo();
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (meta && k === "d" && st.selectedId) {
        e.preventDefault();
        st.duplicateBlock(st.selectedId);
        return;
      }
      if (e.key === "Escape") {
        st.setSelectedId(null);
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && st.selectedId) {
        e.preventDefault();
        requestDelete(st.selectedId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [st, doSave, requestDelete]);

  useEffect(() => {
    if (!st.dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [st.dirty]);

  useEffect(() => {
    document.title = `${st.page?.title ?? "Designer"} · Design | AurumIQ Admin`;
  }, [st.page?.title]);

  /* ---------- render ---------- */

  const formPath = `/cms/pages/${pageId}`;
  if (st.loading)
    return (
      <div className="grid h-screen place-items-center bg-muted text-muted-foreground text-sm">
        Loading designer…
      </div>
    );
  if (st.error || !st.page)
    return (
      <div className="grid h-screen place-items-center bg-muted text-muted-foreground text-sm">
        <div className="grid gap-3 text-center">
          <p>Could not load this page.</p>
          <Button asChild variant="outline">
            <Link to="/cms/pages">Back to pages</Link>
          </Button>
        </div>
      </div>
    );

  const page = st.page;
  const preview = st.mode === "preview";
  const showLeft = !preview && leftTab !== null;
  const showInspector = !preview && inspectorOpen && !!st.selected;
  const livePath = `${env.landingUrl}/${savedSlug === "home" ? "" : savedSlug}`;
  const published = !!st.detail?.published;

  const deleting = confirmDelete ? page.blocks.find((b) => b.id === confirmDelete) : null;
  const deletingTitle = deleting ? blockTitle(deleting) : "";

  const exit = () => {
    if (st.dirty) setConfirmExit(true);
    else navigate(formPath);
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-muted text-[13px]">
      <Toaster theme={theme} />
      <Canvas
        frameRef={frameRef}
        src={src}
        device={st.device}
        ready={frameReady}
        error={previewToken.isError ? "Preview token unavailable" : null}
      />

      {!preview ? (
        <header
          role="toolbar"
          aria-label="Designer toolbar"
          className="absolute top-3 left-1/2 z-40 flex max-w-[calc(100vw-24px)] -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-navy/95 px-2 py-1.5 text-white shadow-[0_18px_50px_-18px_rgba(20,33,61,0.7)] backdrop-blur-md"
        >
          <button type="button" onClick={exit} className={pillBtn} title="Exit designer">
            <ArrowLeft className="size-4" />
            <span className="hidden sm:inline">Exit</span>
          </button>
          <Divider />
          <div className="flex min-w-0 items-center gap-2 px-1">
            <span className="min-w-[48px] max-w-[200px] truncate font-bold text-[13px]">
              {page.title}
            </span>
            <StatusTag variant={published ? "ok" : "warn"}>
              {published ? "PUBLISHED" : "DRAFT"}
            </StatusTag>
            {st.invalidCount ? (
              <StatusTag variant="err">{st.invalidCount} INVALID</StatusTag>
            ) : null}
            {st.dirty ? (
              <span
                role="status"
                className="text-[11px] text-gold"
                title="Unsaved changes"
                aria-label="Unsaved changes"
              >
                ●
              </span>
            ) : null}
          </div>
          <Divider />
          <button
            type="button"
            onClick={st.undo}
            disabled={!st.canUndo}
            title="Undo (⌘Z)"
            aria-label="Undo"
            className="rounded-full p-1.5 text-white/80 hover:bg-white/10 disabled:opacity-30"
          >
            <Undo2 className="size-4" />
          </button>
          <button
            type="button"
            onClick={st.redo}
            disabled={!st.canRedo}
            title="Redo (⌘⇧Z)"
            aria-label="Redo"
            className="rounded-full p-1.5 text-white/80 hover:bg-white/10 disabled:opacity-30"
          >
            <Redo2 className="size-4" />
          </button>
          <Divider />
          <div className="flex items-center gap-0.5 rounded-full bg-white/10 p-0.5">
            {DEVICES.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => st.setDevice(d.key)}
                title={d.label}
                aria-label={d.label}
                aria-pressed={st.device === d.key}
                className={cn(
                  "rounded-full p-1.5 transition",
                  st.device === d.key ? "bg-white text-navy" : "text-white/70 hover:text-white",
                )}
              >
                <d.icon className="size-4" />
              </button>
            ))}
          </div>
          <Divider />
          <div className="flex items-center gap-0.5 rounded-full bg-white/10 p-0.5">
            <button
              type="button"
              onClick={() => st.setMode("edit")}
              aria-pressed={st.mode === "edit"}
              className="flex items-center gap-1 rounded-full bg-white px-2 py-1 font-semibold text-[12px] text-navy"
            >
              <PenLine className="size-3.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => st.setMode("preview")}
              aria-pressed={false}
              title="Preview (hides the editor UI)"
              className="flex items-center gap-1 rounded-full px-2 py-1 font-semibold text-[12px] text-white/70 hover:text-white"
            >
              <Eye className="size-3.5" /> Preview
            </button>
          </div>
          <a
            href={livePath}
            target="_blank"
            rel="noreferrer"
            className={pillBtn}
            title="Open the live page"
          >
            <ExternalLink className="size-4" />
            <span className="hidden lg:inline">Open live</span>
          </a>
          <Link to={formPath} className={pillBtn} title="Switch to the form editor">
            <ListChecks className="size-4" />
            <span className="hidden lg:inline">Form view</span>
          </Link>
          <Divider />
          <Button
            size="sm"
            variant="gold"
            className="shrink-0 rounded-full"
            onClick={() => void doSave()}
            disabled={!st.dirty || st.saving}
            title="Save (⌘S)"
          >
            <Save /> {st.saving ? "Saving…" : "Save"}
          </Button>
          <Button
            size="sm"
            className="shrink-0 rounded-full border border-white/20 bg-transparent text-white hover:bg-white/10"
            onClick={() => setPublishOpen(true)}
            disabled={st.invalidCount > 0 || st.publishing}
          >
            <Rocket /> Publish
          </Button>
        </header>
      ) : null}

      {!preview ? (
        <>
          <nav
            aria-label="Designer panels"
            className="absolute top-1/2 left-3 z-40 flex -translate-y-1/2 flex-col items-center gap-1 rounded-2xl border bg-card/95 p-1.5 shadow-[0_18px_50px_-18px_rgba(20,33,61,0.45)] backdrop-blur"
          >
            {LEFT_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setLeftTab(leftTab === t.key ? null : t.key)}
                title={t.label}
                aria-label={t.label}
                aria-pressed={leftTab === t.key}
                className={cn(
                  railBtn,
                  leftTab === t.key
                    ? "bg-navy text-white"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <t.icon className="size-4" />
                {t.label}
              </button>
            ))}
          </nav>
          {showLeft ? (
            <aside
              aria-label={`${LEFT_TABS.find((t) => t.key === leftTab)?.label} panel`}
              className="absolute top-16 bottom-4 left-[76px] z-30 flex w-[320px] max-w-[calc(100vw-100px)] flex-col overflow-hidden rounded-2xl border bg-card shadow-[0_24px_60px_-24px_rgba(20,33,61,0.5)]"
            >
              <button
                type="button"
                onClick={() => setLeftTab(null)}
                className="absolute top-2 right-2 z-10 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Close panel"
              >
                <X className="size-4" />
              </button>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {leftTab === "add" ? (
                  <AddPanel
                    st={st}
                    onInserted={(label) => {
                      setInspectorOpen(true);
                      toast.info(`${label} block added — edit it in the inspector`);
                    }}
                  />
                ) : null}
                {leftTab === "layers" ? <LayersPanel st={st} onScrollTo={scrollTo} /> : null}
                {leftTab === "page" ? <PagePanel st={st} /> : null}
                {leftTab === "design" ? <DesignPanel /> : null}
                {leftTab === "history" ? (
                  <HistoryPanel st={st} onRevert={(rid) => setRevertTo(rid)} />
                ) : null}
              </div>
            </aside>
          ) : null}
        </>
      ) : null}

      {showInspector ? (
        <Inspector
          st={st}
          tab={inspectorTab}
          onTab={setInspectorTab}
          onClose={() => setInspectorOpen(false)}
          onDelete={requestDelete}
        />
      ) : null}
      {!preview && st.selected && !inspectorOpen ? (
        <button
          type="button"
          onClick={() => setInspectorOpen(true)}
          className="absolute top-16 right-3 z-30 rounded-full bg-navy px-3 py-1.5 font-semibold text-[12px] text-white shadow-lg"
        >
          Edit block
        </button>
      ) : null}

      {preview ? (
        <button
          type="button"
          onClick={() => st.setMode("edit")}
          className="absolute bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-navy px-4 py-2 font-semibold text-[13px] text-white shadow-xl"
        >
          <X className="size-4" /> Exit preview
        </button>
      ) : null}

      <MediaPickerDialog
        open={!!picker}
        onOpenChange={(o) => {
          if (!o && picker) answerPicker(null);
        }}
        onPick={(ref) => answerPicker(ref)}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title="Delete block?"
        description={`“${deletingTitle}” is removed when you save. You can undo before saving.`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDelete) st.deleteBlock(confirmDelete);
        }}
      />
      <ConfirmDialog
        open={confirmExit}
        onOpenChange={setConfirmExit}
        title="Discard unsaved changes?"
        description="You have unsaved changes. Leave the designer without saving?"
        confirmLabel="Discard & exit"
        onConfirm={() => navigate(formPath)}
      />
      <ReasonDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={`Publish “${page.title}”?`}
        description={
          st.dirty
            ? "Unsaved changes are saved as a new revision first, then that revision goes live."
            : `Revision v${st.detail?.draft.version ?? "?"} goes live on the landing site.`
        }
        confirmLabel="Publish"
        confirmVariant="default"
        minLength={0}
        reasonLabel="Note"
        onConfirm={(reason) => st.publish(reason || undefined)}
      />
      <ReasonDialog
        open={revertTo !== null}
        onOpenChange={(o) => !o && setRevertTo(null)}
        title="Revert draft to this revision?"
        description="The draft is replaced by a copy of the selected revision. Unsaved changes are lost; the live page is unaffected until you publish."
        confirmLabel="Revert"
        minLength={0}
        reasonLabel="Note"
        onConfirm={async () => {
          if (revertTo) await st.revert(revertTo);
        }}
      />
    </div>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-white/15" />;
}
